
import { useEffect, useState } from "react";
import { useUsers } from "../../context/UsersContext";
import {
  Users as UsersIcon,
  Search,
  Trash2,
  Mail,
  Phone,
  UserRound,
  LoaderCircle,
  AlertCircle,
  CheckCircle,
  X,
  UserX,
} from "lucide-react";

export default function Users() {
  const {
    users = [],
    loading,
    error,
    success,
    fetchUsers,
    clearError,
    clearSuccess,
    removeUsers,
    role,
  } = useUsers();

  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [localError, setLocalError] = useState("");
  const [localSuccess, setLocalSuccess] = useState("");

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const safeUsers = Array.isArray(users) ? users : [];

  const filteredUsers = safeUsers.filter((user) => {
    const search = searchTerm.toLowerCase().trim();

    if (!search) return true;

    return [
      user.firstName,
      user.lastName,
      user.email,
      user.phone,
    ].some((value) =>
      String(value || "").toLowerCase().includes(search)
    );
  });

  const handleDelete = async (user) => {
    const fullName =
      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      user.email ||
      "cet utilisateur";

    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer ${fullName} ?`
      )
    ) {
      return;
    }

    if (typeof removeUsers !== "function") {
      setLocalError(
        "La fonction de suppression n'est pas disponible dans UsersContext."
      );
      return;
    }

    setDeletingId(user.id);
    setLocalError("");
    setLocalSuccess("");

    try {
      await removeUsers(user.id);
      setLocalSuccess("Client supprimé avec succès.");
    } catch (err) {
      setLocalError(
        err.message || "Impossible de supprimer ce client."
      );
    } finally {
      setDeletingId(null);
    }
  };

  const displayedError = localError || error;
  const displayedSuccess = localSuccess || success;

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-7">

        {/* En-tête */}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-violet-600">
              Administration
            </p>

            <h1 className="text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl">
              Gestion des clients
            </h1>

            <p className="mt-2 text-sm text-stone-500">
              Consulte et gère les utilisateurs de ta boutique.
            </p>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50">
              <UsersIcon size={20} className="text-violet-600" />
            </div>

            <div>
              <p className="text-xl font-bold text-stone-900">
                {safeUsers.length}
              </p>
              <p className="text-xs text-stone-500">
                Client{safeUsers.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </header>

        {/* Messages d'erreur */}
        {displayedError && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          >
            <AlertCircle size={19} className="mt-0.5 shrink-0" />

            <p className="flex-1">{displayedError}</p>

            <button
              type="button"
              onClick={() => {
                setLocalError("");
                clearError?.();
              }}
              aria-label="Fermer le message d'erreur"
              className="rounded-lg p-1 transition hover:bg-red-100"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {/* Message de succès */}
        {displayedSuccess && (
          <div
            role="status"
            className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700"
          >
            <CheckCircle size={19} className="mt-0.5 shrink-0" />

            <p className="flex-1">{displayedSuccess}</p>

            <button
              type="button"
              onClick={() => {
                setLocalSuccess("");
                clearSuccess?.();
              }}
              aria-label="Fermer le message de succès"
              className="rounded-lg p-1 transition hover:bg-emerald-100"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {/* Tableau des clients */}
        <section className="overflow-hidden rounded-2xl border border-stone-200/80 bg-white shadow-sm">

          {/* Barre de recherche */}
          <div className="flex flex-col gap-4 border-b border-stone-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div>
              <h2 className="text-base font-bold text-stone-900">
                Liste des clients
              </h2>
              <p className="mt-1 text-sm text-stone-500">
                {filteredUsers.length} résultat
                {filteredUsers.length !== 1 ? "s" : ""}
              </p>
            </div>

            <div className="relative w-full sm:max-w-sm">
              <Search
                size={18}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400"
              />

              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un client..."
                aria-label="Rechercher un client"
                className="w-full rounded-xl border border-stone-200 bg-stone-50 py-3 pl-10 pr-4 text-sm text-stone-800 outline-none transition placeholder:text-stone-400 focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10"
              />
            </div>
          </div>

          {/* Chargement */}
          {loading ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3">
              <LoaderCircle
                size={30}
                className="animate-spin text-violet-600"
              />
              <p className="text-sm text-stone-500">
                Chargement des clients...
              </p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center px-5 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-stone-100">
                <UserX size={26} className="text-stone-400" />
              </div>

              <h3 className="font-semibold text-stone-800">
                {searchTerm
                  ? "Aucun résultat"
                  : "Aucun client disponible"}
              </h3>

              <p className="mt-2 max-w-sm text-sm text-stone-500">
                {searchTerm
                  ? "Essaie un autre nom, une autre adresse e-mail ou un numéro de téléphone."
                  : "Les clients enregistrés apparaîtront ici."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50/70">
                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-stone-500">
                      Client
                    </th>

                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-stone-500">
                      E-mail
                    </th>

                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-stone-500">
                      Téléphone
                    </th>

                    {role === "admin" && (
                      <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-stone-500">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody className="divide-y divide-stone-100">
                  {filteredUsers.map((user) => {
                    const fullName =
                      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                      "Client";

                    const initials =
                      `${user.firstName?.charAt(0) || ""}${user.lastName?.charAt(0) || ""}`
                        .toUpperCase() || "C";

                    return (
                      <tr
                        key={user.id}
                        className="transition hover:bg-stone-50/70"
                      >
                        {/* Nom */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">
                              {initials}
                            </div>

                            <div>
                              <p className="text-sm font-semibold text-stone-800">
                                {fullName}
                              </p>
                              <p className="mt-1 text-xs text-stone-400">
                                Client
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* E-mail */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-sm text-stone-600">
                            <Mail
                              size={16}
                              className="shrink-0 text-stone-400"
                            />
                            <span className="break-all">
                              {user.email || "Non renseigné"}
                            </span>
                          </div>
                        </td>

                        {/* Téléphone */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-sm text-stone-600">
                            <Phone
                              size={16}
                              className="shrink-0 text-stone-400"
                            />
                            <span>
                              {user.phone || "Non renseigné"}
                            </span>
                          </div>
                        </td>

                        {/* Actions */}
                        {role === "admin" && (
                          <td className="px-6 py-4 text-right">
                            <button
                              type="button"
                              onClick={() => handleDelete(user)}
                              disabled={deletingId === user.id}
                              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-xs font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {deletingId === user.id ? (
                                <LoaderCircle
                                  size={15}
                                  className="animate-spin"
                                />
                              ) : (
                                <Trash2 size={15} />
                              )}

                              {deletingId === user.id
                                ? "Suppression..."
                                : "Supprimer"}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pied de tableau */}
          {!loading && filteredUsers.length > 0 && (
            <div className="flex flex-col gap-2 border-t border-stone-100 bg-stone-50/50 px-6 py-4 text-xs text-stone-500 sm:flex-row sm:items-center sm:justify-between">
              <span>
                {filteredUsers.length} client
                {filteredUsers.length !== 1 ? "s" : ""} affiché
                {filteredUsers.length !== 1 ? "s" : ""}
              </span>

              <span className="inline-flex items-center gap-1.5">
                <UserRound size={14} />
                Gestion des utilisateurs
              </span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
